import { Spacing } from '@persona-id/design-tokens';
import { useState } from 'react';
import styled from 'styled-components';

import { Blueprint, BlueprintBlockType } from 'dashboard/models/blueprint';
import { Environment } from 'dashboard/models/environment';
import { Organization } from 'dashboard/models/organization';
import {
  DashboardPageLink,
  GuideStepTipBanner,
} from 'dashboard/views/DashboardSandboxHomeView/constants';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { getRootDomain } from 'lib/utils/url';

interface RunTestInquiryStepProps {
  currentEnvironment: Environment;
  organization: Organization;
  selectedBlueprint: Blueprint;
  isStepCompleted: boolean;
  isLoadingInquiriesForSelectedBlueprint: boolean;
  handleLoadInquiriesForSelectedBlueprint: (inquiryTemplateIds: string[]) => Promise<void>;
}

const RunTestInquiryStep = ({
  currentEnvironment,
  organization,
  selectedBlueprint,
  isStepCompleted,
  isLoadingInquiriesForSelectedBlueprint,
  handleLoadInquiriesForSelectedBlueprint,
}: RunTestInquiryStepProps) => {
  const [isStepStatusRefreshed, setIsStepStatusRefreshed] = useState(false);

  return (
    <>
      <Paragraph color='primary'>
        You’ve just seen a preview of what your Inquiry looks like. Now, let’s run a test Inquiry
        and go through the flow. You’ll see how Persona collects identity information from your
        customers in a secure and seamless way, and stores it in your Persona Dashboard.
      </Paragraph>
      <Spacer height={2} />
      <Paragraph color='primary'>
        Optional: Try creating and running two separate Inquiries. When you run the first Inquiry,
        set the toggle at the bottom of the Inquiry to “Pass verifications.” When you run the second
        Inquiry, set the toggle to “Fail verifications.”
      </Paragraph>
      <Spacer height={2} />
      <ButtonRow>
        <Button
          id='home-sandbox-onboarding-checklist__run-test-inquiry'
          width='button'
          onClick={() => {
            let url = DashboardPageLink.PreviewInquiry as string;
            const inquiryTemplateBlocks = selectedBlueprint.attributes.blocks.filter(
              (block) => block.type === BlueprintBlockType.InquiryTemplate,
            );
            if (inquiryTemplateBlocks.length > 0) {
              const inquiryTemplateToken = inquiryTemplateBlocks[0].token;
              url = `${location.protocol}//${[
                organization.attributes.subdomain,
                getRootDomain(location.host),
              ]
                .filter(Boolean)
                .join('.')}/verify?inquiry-template-id=${inquiryTemplateToken}&environment-id=${
                currentEnvironment.id
              }`;
            }
            window.open(url);
          }}
        >
          {isStepCompleted ? 'Run another test Inquiry' : 'Run a test Inquiry'}
        </Button>
        {!isStepCompleted && (
          <Button
            id='home-sandbox-onboarding-checklist__refresh-inquiry-list-for-test-inquiry'
            disabled={isLoadingInquiriesForSelectedBlueprint}
            variant='basic'
            width='button'
            onClick={() => {
              const inquiryTemplateBlocks = selectedBlueprint.attributes.blocks.filter(
                (block) => block.type === BlueprintBlockType.InquiryTemplate,
              );
              if (inquiryTemplateBlocks.length > 0) {
                handleLoadInquiriesForSelectedBlueprint(
                  inquiryTemplateBlocks.map((itmpl) => itmpl.token),
                );
                setIsStepStatusRefreshed(true);
              }
            }}
          >
            Mark step completed
          </Button>
        )}
      </ButtonRow>
      {isStepStatusRefreshed && !isStepCompleted && (
        <>
          <Spacer height={2} />
          <Paragraph color='secondary'>
            Looks like you haven’t finished any Inquiries yet! (Did you get to the end of the flow?)
          </Paragraph>
        </>
      )}
      <Spacer height={2} />
      <GuideStepTipBanner>
        <Paragraph color='primary'>
          Tip: Another way to try out an Inquiry is to use{' '}
          <a
            href='https://support.withpersona.com/hc/en-us/articles/14446163889939-How-to-preview-an-Inquiry'
            target='_blank'
            rel='noreferrer'
          >
            Preview
          </a>
          .
        </Paragraph>
      </GuideStepTipBanner>
    </>
  );
};

const ButtonRow = styled('div')`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[2]};
`;

export default RunTestInquiryStep;
